<script setup lang="ts">
import { Plus, X } from "@lucide/vue";
import type { PopupTriggerConditions } from "@aswin.dev/types";
import { ref, watch } from "vue";
import { useI18n } from "../composables/useI18n";

const props = defineProps<{
  conditions: PopupTriggerConditions;
}>();

const emit = defineEmits<{
  patch: [partial: Partial<PopupTriggerConditions>];
}>();

const { t } = useI18n();

/** Second row (AND/OR + page views) — matches “Add rule” / delete UX in the reference UI. */
const showPageViewsRow = ref(props.conditions.pageViews > 0);

watch(
  () => props.conditions.pageViews,
  (n) => {
    if (n > 0) {
      showPageViewsRow.value = true;
    }
  },
);

function patchDelay(ev: Event): void {
  const raw = (ev.target as HTMLInputElement).value;
  emit("patch", {
    delaySeconds: Math.max(0, Number(raw) || 0),
  });
}

function patchPageViews(ev: Event): void {
  const raw = (ev.target as HTMLInputElement).value.trim();
  emit("patch", {
    pageViews: raw === "" ? 0 : Math.max(0, Number(raw) || 0),
  });
}

function setOperator(op: "and" | "or"): void {
  emit("patch", { operator: op });
}

function addPageViewsRule(): void {
  showPageViewsRow.value = true;
}

function removePageViewsRule(): void {
  showPageViewsRow.value = false;
  emit("patch", { pageViews: 0 });
}
</script>

<template>
  <div
    class="tpl:mt-3 tpl:space-y-3 tpl:border-t tpl:border-[var(--tpl-border)] tpl:pt-4"
  >
    <!-- Row 1: delay + unit (dropdown like reference) -->
    <div
      class="tpl:flex tpl:flex-wrap tpl:items-center tpl:gap-x-2 tpl:gap-y-2 tpl:text-sm tpl:text-[var(--tpl-text-muted)]"
    >
      <span class="tpl:shrink-0 tpl:text-[var(--tpl-text)]">{{
        t.popupDisplayRules.triggerShowOnlyAfter
      }}</span>
      <input
        type="number"
        min="0"
        inputmode="numeric"
        class="tpl:h-9 tpl:w-14 tpl:shrink-0 tpl:rounded-lg tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)] tpl:px-2 tpl:text-center tpl:text-sm tpl:text-[var(--tpl-text)] tpl:tabular-nums tpl:outline-none focus:tpl:ring-2 focus:tpl:ring-[var(--tpl-primary)]/25"
        :value="props.conditions.delaySeconds"
        @change="patchDelay"
      />
      <select
        class="tpl:h-9 tpl:min-w-[10.5rem] tpl:shrink-0 tpl:cursor-pointer tpl:appearance-none tpl:rounded-lg tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)] tpl:pl-3 tpl:pr-8 tpl:text-left tpl:text-sm tpl:text-[var(--tpl-text)] tpl:outline-none focus:tpl:ring-2 focus:tpl:ring-[var(--tpl-primary)]/25"
        aria-label="Time condition unit"
      >
        <option value="seconds">
          {{ t.popupDisplayRules.triggerSecondsOnPage }}
        </option>
      </select>
    </div>

    <!-- Row 2: AND/OR + page views + delete -->
    <div
      v-if="showPageViewsRow"
      class="tpl:flex tpl:flex-wrap tpl:items-center tpl:gap-x-2 tpl:gap-y-2"
    >
      <div
        class="trigger-operator-toggle"
        role="group"
        :aria-label="t.popupDisplayRules.triggerOperatorGroupAria"
      >
        <button
          type="button"
          class="trigger-operator-toggle__btn"
          :class="{
            'trigger-operator-toggle__btn--selected':
              props.conditions.operator === 'and',
          }"
          @click="setOperator('and')"
        >
          {{ t.popupDisplayRules.operatorAndShort }}
        </button>
        <button
          type="button"
          class="trigger-operator-toggle__btn"
          :class="{
            'trigger-operator-toggle__btn--selected':
              props.conditions.operator === 'or',
          }"
          @click="setOperator('or')"
        >
          {{ t.popupDisplayRules.operatorOrShort }}
        </button>
      </div>
      <input
        type="number"
        min="0"
        inputmode="numeric"
        class="tpl:h-9 tpl:w-14 tpl:shrink-0 tpl:rounded-lg tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)] tpl:px-2 tpl:text-center tpl:text-sm tpl:text-[var(--tpl-text)] tpl:tabular-nums tpl:outline-none focus:tpl:ring-2 focus:tpl:ring-[var(--tpl-primary)]/25"
        :value="
          props.conditions.pageViews > 0 ? props.conditions.pageViews : ''
        "
        :placeholder="'0'"
        @change="patchPageViews"
      />
      <span class="tpl:text-sm tpl:text-[var(--tpl-text-muted)]">{{
        t.popupDisplayRules.triggerPageViewsSuffix
      }}</span>
      <button
        type="button"
        class="tpl:ml-auto tpl:flex tpl:h-8 tpl:w-8 tpl:shrink-0 tpl:items-center tpl:justify-center tpl:rounded-lg tpl:text-[var(--tpl-text-muted)] tpl:transition-colors hover:tpl:bg-[var(--tpl-bg-hover)] hover:tpl:text-[var(--tpl-text)]"
        :aria-label="t.popupDisplayRules.triggerRemoveRuleAria"
        @click="removePageViewsRule"
      >
        <X :size="16" :stroke-width="2" />
      </button>
    </div>

    <!-- Add rule (when page-views row is hidden) -->
    <button
      v-if="!showPageViewsRow"
      type="button"
      class="trigger-add-rule-btn"
      @click="addPageViewsRule"
    >
      <span class="trigger-add-rule-btn__icon" aria-hidden="true">
        <Plus :size="15" :stroke-width="2.25" />
      </span>
      {{ t.popupDisplayRules.triggerAddRule }}
    </button>
  </div>
</template>

<style scoped>
/* Segmented AND / OR — light rail, dark selected pill, white unselected with subtle border */
.trigger-operator-toggle {
  display: inline-flex;
  flex-shrink: 0;
  align-items: stretch;
  gap: 0.25rem;
  padding: 0.1875rem;
  background: var(--tpl-bg, #ffffff);
  border: 1px solid var(--tpl-border, #d8d8d8);
  border-radius: 9999px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
}

.trigger-operator-toggle__btn {
  margin: 0;
  padding: 0.4375rem 0.8125rem;
  font-family: inherit;
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--tpl-text-muted, #6b6b6b);
  background: transparent;
  border: 1px solid transparent;
  border-radius: 9999px;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.trigger-operator-toggle__btn:hover:not(
    .trigger-operator-toggle__btn--selected
  ) {
  color: var(--tpl-text, #333333);
  background: rgba(15, 23, 42, 0.04);
  border-color: var(--tpl-border, #c8c8c8);
}

.trigger-operator-toggle__btn--selected {
  color: #ffffff;
  background: var(--tpl-text, #333333);
  border-color: transparent;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
}

.trigger-operator-toggle__btn:not(.trigger-operator-toggle__btn--selected) {
  background: var(--tpl-bg, #ffffff);
  border-color: var(--tpl-border, #d8d8d8);
}

.trigger-operator-toggle__btn:focus {
  outline: none;
}

.trigger-operator-toggle__btn:focus-visible {
  outline: 2px solid var(--tpl-primary, #6366f1);
  outline-offset: 2px;
}

/* Matches reference “+ Add rule”: white pill, hairline border, soft shadow, muted + */
.trigger-add-rule-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  margin: 0;
  padding: 0.5rem 0.875rem;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.25;
  letter-spacing: 0.01em;
  color: var(--tpl-text, #3a3a3a);
  background: var(--tpl-bg, #ffffff);
  border: 1px solid var(--tpl-border, #d8d8d8);
  border-radius: 8px;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease,
    box-shadow 0.15s ease,
    color 0.15s ease;
}

.trigger-add-rule-btn:hover {
  background: var(--tpl-bg-hover, #f7f7f7);
  border-color: var(--tpl-border, #c8c8c8);
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
}

.trigger-add-rule-btn:active {
  box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.06) inset;
}

.trigger-add-rule-btn:focus {
  outline: none;
}

.trigger-add-rule-btn:focus-visible {
  outline: 2px solid var(--tpl-primary, #6366f1);
  outline-offset: 2px;
}

.trigger-add-rule-btn__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--tpl-text-muted, #6b6b6b);
  line-height: 0;
}

.trigger-add-rule-btn:hover .trigger-add-rule-btn__icon {
  color: var(--tpl-text, #3a3a3a);
}
</style>
